import { useEffect, useMemo, useState } from "react";
import { fetchClasses, fetchNear, fetchStats, fetchFilters, fetchLiveStatus, csvDownloadUrl } from "@/lib/api";
import { Hero } from "@/components/Hero";
import { StatsStrip } from "@/components/StatsStrip";
import { FilterBar } from "@/components/FilterBar";
import { ClassCard } from "@/components/ClassCard";
import { ClassDetailModal } from "@/components/ClassDetailModal";
import { CategoryRail } from "@/components/CategoryRail";
import { Footer } from "@/components/Footer";
import { MapView } from "@/components/MapView";
import { ClaimStudioModal } from "@/components/ClaimStudioModal";
import { NextWindowStrip } from "@/components/NextWindowStrip";
import { NextFifteen } from "@/components/NextFifteen";
import { ChatPanel } from "@/components/ChatPanel";
import { Button } from "@/components/ui/button";
import {
    Download,
    Loader2,
    FlagTriangleRight,
    LayoutGrid,
    Map as MapIcon,
    Locate,
    Flame,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FILTERS = {
    search: "",
    category: "All",
    borough: "All",
    women_focus: "All",
    skill_level: "All",
    manual_review: null,
};

const RADIUS_OPTIONS = [1, 2, 5, 10];

export default function HomePage() {
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [classes, setClasses] = useState([]);
    const [stats, setStats] = useState(null);
    const [filterOpts, setFilterOpts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [viewMode, setViewMode] = useState("grid"); // grid | map
    const [claimTarget, setClaimTarget] = useState(null);
    // Proximity sort + live overlay state
    const [sortBy, setSortBy] = useState("default"); // 'default' | 'distance'
    const [userPos, setUserPos] = useState(null);
    const [radius, setRadius] = useState(2);
    const [includeLive, setIncludeLive] = useState(true);
    const [liveAvailable, setLiveAvailable] = useState(false);
    const [startsSoonOnly, setStartsSoonOnly] = useState(false);
    // Grid pagination — 25 cards per page.
    const PAGE_SIZE = 25;
    const [page, setPage] = useState(0);

    // Reset to the first page whenever the *result set* changes.
    useEffect(() => {
        setPage(0);
    }, [filters, sortBy, userPos, radius, includeLive, startsSoonOnly]);

    useEffect(() => {
        fetchLiveStatus().then((s) => setLiveAvailable(!!s?.configured)).catch(() => {});
    }, []);

    // Approximate centroids for each NYC borough — used as a fallback when
    // geolocation fails (denied, blocked by iframe policy, or unsupported).
    const BOROUGH_CENTROIDS = {
        Manhattan: [40.7831, -73.9712],
        Brooklyn: [40.6782, -73.9442],
        Queens: [40.7282, -73.7949],
        Bronx: [40.8448, -73.8648],
        "Staten Island": [40.5795, -74.1502],
    };

    const pickBorough = (name) => {
        const coords = BOROUGH_CENTROIDS[name];
        if (!coords) return;
        setUserPos(coords);
        setSortBy("distance");
        toast.success(`Centered on ${name}. Sorting by distance.`);
    };

    const locateMe = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation not supported. Pick a borough instead.", {
                action: { label: "Manhattan", onClick: () => pickBorough("Manhattan") },
            });
            return;
        }
        toast("Finding your location…");
        navigator.geolocation.getCurrentPosition(
            (p) => {
                setUserPos([p.coords.latitude, p.coords.longitude]);
                setSortBy("distance");
                toast.success("Located. Sorting by distance.");
            },
            (err) => {
                // Pick the best recovery copy for each PositionError code.
                let msg;
                if (err.code === 1) {
                    msg = "Location is blocked. Tap the lock icon in the address bar to allow it — or pick a borough below.";
                } else if (err.code === 2) {
                    msg = "Couldn't determine your location. Pick a borough instead.";
                } else if (err.code === 3) {
                    msg = "Location request timed out. Try again or pick a borough.";
                } else {
                    msg = "Couldn't get your location. Pick a borough instead.";
                }
                toast.error(msg, {
                    duration: 7000,
                    action: { label: "Manhattan", onClick: () => pickBorough("Manhattan") },
                });
            },
            // enableHighAccuracy=false gives a fast city-level fix; that's
            // accurate enough for radius filtering at 1–10mi.
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60_000 }
        );
    };

    // Allow the curated detail modal to trigger the claim modal via a window event.
    useEffect(() => {
        const handler = (e) => setClaimTarget(e.detail);
        window.addEventListener("claim:open", handler);
        return () => window.removeEventListener("claim:open", handler);
    }, []);

    useEffect(() => {
        fetchStats().then(setStats).catch(() => toast.error("Failed to load stats"));
        fetchFilters().then(setFilterOpts).catch(() => toast.error("Failed to load filters"));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        // Two fetch modes:
        // 1) Default ordering → /api/classes with filters
        // 2) Closest-to-me (sortBy='distance') → /api/near with lat/lng (and includeLive)
        const useDistance = sortBy === "distance" && userPos;
        const params = { ...filters };
        if (!params.search) delete params.search;
        Object.keys(params).forEach((k) => {
            if (params[k] === "All" || params[k] === null || params[k] === undefined) delete params[k];
        });

        const req = useDistance
            ? fetchNear({
                  lat: userPos[0],
                  lng: userPos[1],
                  radius_miles: radius,
                  limit: 500,
                  include_live: includeLive && liveAvailable,
                  starts_soon_only: startsSoonOnly,
                  ...(filters.category && filters.category !== "All" ? { category: filters.category } : {}),
                  ...(filters.search ? { search: filters.search } : {}),
              })
            : fetchClasses({ ...params, starts_soon_only: startsSoonOnly });

        req
            .then((rows) => {
                if (!cancelled) setClasses(rows);
            })
            .catch(() => toast.error("Failed to load classes"))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [filters, sortBy, userPos, radius, includeLive, liveAvailable, startsSoonOnly]);

    const visible = useMemo(() => classes, [classes]);
    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const pageStart = safePage * PAGE_SIZE;
    const pageEnd = pageStart + PAGE_SIZE;
    const paged = useMemo(
        () => visible.slice(pageStart, pageEnd),
        [visible, pageStart, pageEnd]
    );

    // Keep the user near the top of the grid as they paginate.
    const scrollToGrid = () => {
        document
            .querySelector('[data-testid="directory-section"]')
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onPickCategory = (cat) => setFilters((f) => ({ ...f, category: cat }));

    return (
        <div className="min-h-screen flex flex-col">
            <Hero stats={stats} />

            <CategoryRail
                categories={stats?.by_category || []}
                active={filters.category}
                onPick={onPickCategory}
            />

            <StatsStrip stats={stats} />

            <NextWindowStrip
                startsSoonActive={startsSoonOnly}
                onPickStartsSoon={() => {
                    setStartsSoonOnly(true);
                    document
                        .querySelector('[data-testid="directory-section"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                onClearStartsSoon={() => setStartsSoonOnly(false)}
            />

            <NextFifteen
                onPickClass={(c) => setSelected(c)}
                onBrowseAll={() => {
                    document
                        .querySelector('[data-testid="directory-section"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
            />

            <section className="px-6 md:px-12 pt-10 pb-6 border-t border-border" data-testid="directory-section">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                    <div>
                        <p className="font-mono-label text-xs text-muted-foreground mb-3">
                            The Directory · {visible.length} {visible.length === 1 ? "class" : "classes"}
                            {visible.length > PAGE_SIZE && (
                                <>
                                    {" "}· Page {safePage + 1} of {totalPages}
                                    {" "}<span className="opacity-60">(showing {pageStart + 1}–{Math.min(pageEnd, visible.length)})</span>
                                </>
                            )}
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl">
                            Browse the <span className="font-editorial text-primary">full directory.</span>
                        </h2>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="inline-flex border border-foreground" data-testid="view-toggle">
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`px-4 py-3 font-mono-label text-xs flex items-center gap-2 hover-invert ${
                                    viewMode === "grid" ? "bg-foreground text-background" : "bg-background"
                                }`}
                                data-testid="view-grid-btn"
                            >
                                <LayoutGrid className="h-3.5 w-3.5" /> Grid
                            </button>
                            <button
                                onClick={() => setViewMode("map")}
                                className={`px-4 py-3 font-mono-label text-xs flex items-center gap-2 border-l border-foreground hover-invert ${
                                    viewMode === "map" ? "bg-foreground text-background" : "bg-background"
                                }`}
                                data-testid="view-map-btn"
                            >
                                <MapIcon className="h-3.5 w-3.5" /> Map
                            </button>
                        </div>
                        <Button
                            asChild
                            className="rounded-none uppercase tracking-widest hover-invert border border-foreground bg-foreground text-background"
                            data-testid="download-csv-btn"
                        >
                            <a href={csvDownloadUrl()} download>
                                <Download className="mr-2 h-4 w-4" /> Download CSV
                            </a>
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-none uppercase tracking-widest border-foreground hover-invert"
                            onClick={() =>
                                setFilters((f) => ({ ...f, manual_review: f.manual_review ? null : true }))
                            }
                            data-testid="toggle-manual-review-btn"
                        >
                            <FlagTriangleRight className="mr-2 h-4 w-4" />
                            {filters.manual_review ? "Show All" : "Manual Review"}
                        </Button>
                    </div>
                </div>

                <FilterBar
                    filters={filters}
                    options={filterOpts}
                    onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
                    onReset={() => setFilters(DEFAULT_FILTERS)}
                />

                <div className="mt-4 flex flex-wrap items-center gap-3 border border-border bg-secondary px-4 py-3" data-testid="proximity-bar">
                    <span className="font-mono-label text-[10px] text-muted-foreground">Sort</span>
                    <div className="inline-flex border border-foreground" data-testid="sort-toggle">
                        <button
                            onClick={() => setSortBy("default")}
                            className={`px-3 py-1.5 font-mono-label text-[10px] hover-invert ${
                                sortBy === "default" ? "bg-foreground text-background" : "bg-background"
                            }`}
                            data-testid="sort-default"
                        >
                            Default
                        </button>
                        <button
                            onClick={() => {
                                if (!userPos) return locateMe();
                                setSortBy("distance");
                            }}
                            className={`px-3 py-1.5 font-mono-label text-[10px] border-l border-foreground hover-invert flex items-center gap-1 ${
                                sortBy === "distance" ? "bg-foreground text-background" : "bg-background"
                            }`}
                            data-testid="sort-distance"
                        >
                            <Locate className="h-3 w-3" /> Closest to me
                        </button>
                    </div>

                    {/* Borough fallback — always available, no permissions
                        needed. Hidden once a userPos is already set. */}
                    {!userPos && (
                        <select
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v) pickBorough(v);
                                e.target.value = "";
                            }}
                            defaultValue=""
                            className="px-3 py-1.5 font-mono-label text-[10px] uppercase tracking-widest border border-foreground bg-background hover-invert cursor-pointer"
                            data-testid="borough-picker"
                            aria-label="Pick a borough as your location"
                        >
                            <option value="" disabled>or pick a borough</option>
                            {Object.keys(BOROUGH_CENTROIDS).map((b) => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    )}

                    <button
                        onClick={() => setStartsSoonOnly((s) => !s)}
                        className={`px-3 py-1.5 font-mono-label text-[10px] border flex items-center gap-1 transition-colors ${
                            startsSoonOnly
                                ? "bg-primary text-background border-primary hover:bg-[#E03E00]"
                                : "bg-background border-foreground hover-invert"
                        }`}
                        title="Filter to classes opening or running in the next 60 minutes (NYC time)"
                        data-testid="toggle-starts-soon"
                    >
                        <Flame className="h-3 w-3" /> Starts soon {startsSoonOnly ? "ON" : "OFF"}
                    </button>

                    {sortBy === "distance" && userPos && (
                        <>
                            <span className="font-mono-label text-[10px] text-muted-foreground ml-2">
                                Radius
                            </span>
                            {RADIUS_OPTIONS.map((r) => (
                                <button
                                    key={r}
                                    onClick={() => setRadius(r)}
                                    className={`px-2 py-1 font-mono-label text-[10px] border border-foreground hover-invert ${
                                        radius === r ? "bg-foreground text-background" : "bg-background"
                                    }`}
                                    data-testid={`grid-radius-${r}`}
                                >
                                    {r} mi
                                </button>
                            ))}
                            <button
                                onClick={locateMe}
                                className="ml-auto px-3 py-1.5 font-mono-label text-[10px] border border-foreground hover-invert"
                                data-testid="grid-recenter"
                            >
                                Re-locate
                            </button>
                        </>
                    )}
                </div>

                {viewMode === "map" && (
                    <div className="mt-8 -mx-6 md:-mx-12">
                        <MapView
                            classes={visible}
                            userPos={userPos}
                            radius={sortBy === "distance" && userPos ? radius : null}
                            loading={loading}
                            onSelect={(r) => setSelected(r)}
                            onClaim={(r) => setClaimTarget(r)}
                        />
                    </div>
                )}

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 lg:gap-10 items-start">
                    {viewMode === "grid" && (
                    <div className="min-w-0">
                        {loading && (
                            <div className="flex items-center justify-center py-24 text-muted-foreground">
                                <Loader2 className="animate-spin h-5 w-5 mr-2" /> Loading classes…
                            </div>
                        )}
                        {!loading && visible.length === 0 && (
                            <div className="py-24 text-center text-muted-foreground border border-dashed border-border">
                                <p className="font-mono-label text-xs">No matches</p>
                                <p className="mt-2 font-display text-2xl">
                                    Try adjusting <span className="font-editorial text-primary">your filters.</span>
                                </p>
                            </div>
                        )}
                        {!loading && visible.length > 0 && (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border" data-testid="class-grid">
                                    {paged.map((row) => (
                                        <ClassCard key={row.id} row={row} onClick={() => setSelected(row)} />
                                    ))}
                                </div>
                                {totalPages > 1 && (
                                    <nav
                                        className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6"
                                        aria-label="Pagination"
                                        data-testid="pagination"
                                    >
                                        <div className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                                            Showing <span className="text-foreground">{pageStart + 1}–{Math.min(pageEnd, visible.length)}</span> of {visible.length}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap justify-center">
                                            <button
                                                onClick={() => { setPage((p) => Math.max(0, p - 1)); scrollToGrid(); }}
                                                disabled={safePage === 0}
                                                className="px-3 py-2 font-mono-label text-[10px] uppercase tracking-widest border border-foreground bg-background disabled:opacity-30 disabled:cursor-not-allowed hover-invert"
                                                data-testid="page-prev"
                                            >
                                                ← Prev
                                            </button>
                                            <PageNumbers
                                                page={safePage}
                                                total={totalPages}
                                                onPick={(p) => { setPage(p); scrollToGrid(); }}
                                            />
                                            <button
                                                onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); scrollToGrid(); }}
                                                disabled={safePage >= totalPages - 1}
                                                className="px-3 py-2 font-mono-label text-[10px] uppercase tracking-widest border border-foreground bg-background disabled:opacity-30 disabled:cursor-not-allowed hover-invert"
                                                data-testid="page-next"
                                            >
                                                Next →
                                            </button>
                                        </div>
                                    </nav>
                                )}
                            </>
                        )}
                    </div>
                    )}
                    {viewMode === "map" && (
                        <div className="min-w-0 border border-dashed border-border bg-secondary p-6 hidden lg:block">
                            <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                                Map view
                            </p>
                            <p className="font-display text-2xl leading-tight">
                                Pins are <span className="text-primary">above ↑</span>
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                Switch to Grid to see cards here, or ask Lyve AI for ideas on the right.
                            </p>
                        </div>
                    )}
                    <div className="lg:sticky lg:top-6 lg:self-start" data-testid="chat-column">
                        <ChatPanel
                            userPos={userPos}
                            onPickClass={(c) => setSelected(c)}
                            onLocateMe={locateMe}
                        />
                    </div>
                </div>
            </section>

            <Footer stats={stats} />

            <ClassDetailModal row={selected} onClose={() => setSelected(null)} />
            <ClaimStudioModal
                open={!!claimTarget}
                onClose={() => setClaimTarget(null)}
                prefill={claimTarget || {}}
            />
        </div>
    );
}

/**
 * Editorial page-number row: shows up to 7 page chips with ellipses around the
 * current page. Hidden on the smallest screens — Prev/Next still work there.
 */
function PageNumbers({ page, total, onPick }) {
    const windowSize = 5;
    let start = Math.max(0, page - Math.floor(windowSize / 2));
    let end = Math.min(total, start + windowSize);
    start = Math.max(0, end - windowSize);
    const nums = [];
    for (let i = start; i < end; i++) nums.push(i);

    const Chip = ({ p }) => (
        <button
            onClick={() => onPick(p)}
            className={`hidden sm:inline-flex h-9 min-w-9 items-center justify-center px-2 font-mono-label text-[10px] uppercase tracking-widest border border-foreground hover-invert ${
                p === page ? "bg-foreground text-background" : "bg-background"
            }`}
            data-testid={`page-${p + 1}`}
            aria-current={p === page ? "page" : undefined}
        >
            {p + 1}
        </button>
    );
    const Ellipsis = () => (
        <span className="hidden sm:inline-flex h-9 items-center px-1 font-mono-label text-[10px] text-muted-foreground" aria-hidden>
            …
        </span>
    );

    return (
        <div className="flex items-center gap-1">
            {start > 0 && (
                <>
                    <Chip p={0} />
                    {start > 1 && <Ellipsis />}
                </>
            )}
            {nums.map((p) => (
                <Chip key={p} p={p} />
            ))}
            {end < total && (
                <>
                    {end < total - 1 && <Ellipsis />}
                    <Chip p={total - 1} />
                </>
            )}
        </div>
    );
}
